import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import {
  ReconciliationFindingResponse,
  ReconciliationLatestResponse,
  ReconciliationReportsResponse,
  ReconciliationRunResponse,
} from '../../../Interfaces/api/backoffice/reconciliation.interface';
import { ReconciliationFinding } from '../../../Models/backoffice/reconciliation-finding.model';
import { ReconciliationReport } from '../../../Models/backoffice/reconciliation-report.model';

@Injectable({
  providedIn: 'root',
})
export class ReconciliationService {
  private readonly _apiService = inject(ApiService);

  latestRequest(): TypedResourceRequest<LaravelDefaultResponse<ReconciliationLatestResponse>> {
    return this._apiService.get<LaravelDefaultResponse<ReconciliationLatestResponse>>(
      endpoints.backoffice.reconciliation,
      { skipLoading: true, skipError: true },
    );
  }

  reportsRequest(
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<ReconciliationReportsResponse>> {
    return this._apiService.get<LaravelDefaultResponse<ReconciliationReportsResponse>>(
      endpoints.backoffice.reconciliationReports,
      { params: { page, pageSize }, skipLoading: true, skipError: true },
    );
  }

  inspectRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<ReconciliationFindingResponse>> {
    return this._apiService.get<LaravelDefaultResponse<ReconciliationFindingResponse>>(
      endpoints.backoffice.organizationReconciliation(organizationId),
      { skipLoading: true, skipError: true },
    );
  }

  runPortfolio(): Observable<ReconciliationRunResponse> {
    return this._run(endpoints.backoffice.reconciliationRun);
  }

  runForOrganization(organizationId: string): Observable<ReconciliationRunResponse> {
    return this._run(endpoints.backoffice.organizationReconciliationRun(organizationId));
  }

  toLatestReport(
    response: LaravelDefaultResponse<ReconciliationLatestResponse> | undefined,
  ): ReconciliationReport | null {
    const row = response?.data?.report;

    return row ? new ReconciliationReport(row) : null;
  }

  toShapes(
    response: LaravelDefaultResponse<ReconciliationLatestResponse> | undefined,
  ): string[] {
    return response?.data?.shapes ?? [];
  }

  toReports(
    response: LaravelDefaultResponse<ReconciliationReportsResponse> | undefined,
  ): ReconciliationReport[] {
    return (response?.data?.items ?? []).map((row) => new ReconciliationReport(row));
  }

  toReportTotal(
    response: LaravelDefaultResponse<ReconciliationReportsResponse> | undefined,
  ): number {
    return response?.data?.total ?? 0;
  }

  toFinding(
    response: LaravelDefaultResponse<ReconciliationFindingResponse> | undefined,
  ): ReconciliationFinding | null {
    return response?.data ? new ReconciliationFinding(response.data) : null;
  }

  private _run(resource: string): Observable<ReconciliationRunResponse> {
    const request = this._apiService.post<LaravelDefaultResponse<ReconciliationRunResponse>>(
      resource,
      { skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<ReconciliationRunResponse>>(request)
      .pipe(map((response) => response.data));
  }
}
